import { useRef, useState } from "react";
import { Head } from "@inertiajs/react";
import { ChevronDown } from "lucide-react";
import SchoolLayout from "@/components/schools/SchoolLayout";
import ContactLink from "@/components/ContactLink";
import { Card } from "@/components/ui/card";
import SetupOverview from "@/components/schools/setup/SetupOverview";
import SetupStepper from "@/components/schools/setup/SetupStepper";
import StepPanel from "@/components/schools/setup/StepPanel";
import { SchoolDetailsStep, CampaignStep, PaymentStep, SquareStep } from "@/components/schools/setup/FoundationSteps";
import { RosterStep, TeachersStep, TeamStep } from "@/components/schools/setup/PeopleSteps";
import { SchoolRewardsStep, StudentPrizesStep } from "@/components/schools/setup/RewardSteps";
import { TeacherInfoStep, FamilyInfoStep, TakeHomeSheetsStep, NotificationsStep } from "@/components/schools/setup/WordSteps";
import { LaunchStep } from "@/components/schools/setup/LaunchStep";

const STEP_COMPONENTS = {
  school_details: SchoolDetailsStep,
  campaign: CampaignStep,
  payment: PaymentStep,
  square: SquareStep,
  roster: RosterStep,
  teachers: TeachersStep,
  team: TeamStep,
  school_rewards: SchoolRewardsStep,
  student_prizes: StudentPrizesStep,
  teacher_info: TeacherInfoStep,
  family_info: FamilyInfoStep,
  take_home_sheets: TakeHomeSheetsStep,
  notifications: NotificationsStep,
  launch: LaunchStep,
};

/**
 * The setup guide: every step between signing up and launch, in order,
 * with what's required, what's recommended, and when to aim for. Opens
 * on the next step to do (or ?step=). See docs/BUSINESS_RULES.md §2.
 */
export default function Setup({ school, membership, setup, initial_step }) {
  const [activeKey, setActiveKey] = useState(initial_step);
  const [stepsOpen, setStepsOpen] = useState(false);
  const panelRef = useRef(null);
  const { steps } = setup;
  const step = steps.find((s) => s.key === activeKey) || steps[0];
  const StepBody = STEP_COMPONENTS[step.key];

  const select = (key) => {
    setActiveKey(key);
    setStepsOpen(false);
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    requestAnimationFrame(() => {
      const top = panelRef.current?.getBoundingClientRect().top;
      if (top !== undefined && top < 0) panelRef.current.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
    });
  };

  return (
    <SchoolLayout
      school={school}
      membership={membership}
      eyebrow="Setup guide"
      title={setup.campaign ? `Getting ${setup.campaign.name} ready` : "Let's set up your read-a-thon"}
      hideSetupBanner
    >
      <Head title="Setup guide" />

      <SetupOverview setup={setup} activeKey={step.key} onSelect={select} />

      <div className="mt-8 grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[16rem_minmax(0,1fr)] lg:items-start">
        <aside className="lg:sticky lg:top-6">
          {/* Phones and tablets: the step list folds away above the panel. */}
          <button
            type="button"
            onClick={() => setStepsOpen(!stepsOpen)}
            aria-expanded={stepsOpen}
            className="flex w-full items-center justify-between rounded-xl border border-border bg-card px-4 py-3 text-sm font-medium text-foreground lg:hidden"
          >
            <span>All steps · {setup.progress.complete}/{setup.progress.total} done</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${stepsOpen ? "rotate-180" : ""}`} />
          </button>
          <Card className={`mt-2 p-3 lg:mt-0 lg:block ${stepsOpen ? "block" : "hidden"}`}>
            <SetupStepper steps={steps} activeKey={step.key} onSelect={select} />
            <p className="mt-4 border-t border-border px-3 pt-3 text-xs leading-relaxed text-muted-foreground">
              Your progress is saved as you go. Stuck?{" "}
              <ContactLink className="font-medium text-primary hover:underline">Get in touch</ContactLink> and we'll help you set up.
            </p>
          </Card>
        </aside>

        <div ref={panelRef} className="scroll-mt-6">
          <StepPanel
            step={step}
            steps={steps}
            today={setup.key_dates.today}
            skipPath={setup.paths.skip_step?.[step.key]}
            onSelect={select}
          >
            <StepBody key={step.key} step={step} steps={steps} setup={setup} onSelect={select} />
          </StepPanel>
        </div>
      </div>
    </SchoolLayout>
  );
}
